import { addIsoDays, type CalendarDueItem, type CalendarOccurrence, weekdayIndex } from "@probetalab/contracts";
import type { ChipTone } from "../components/ui.js";

/**
 * Pure helpers of the Calendario page (ADR 0027): which days a view covers, how a day is named,
 * where a tap on a study goes, and how its state reads. Days are YYYY-MM-DD, lab-local.
 */

export type CalendarMode = "day" | "week" | "month";

export function weekStart(isoDay: string): string {
  return addIsoDays(isoDay, -weekdayIndex(isoDay));
}

export function monthStart(isoDay: string): string {
  return `${isoDay.slice(0, 7)}-01`;
}

export function monthEnd(isoDay: string): string {
  const y = Number(isoDay.slice(0, 4));
  const m = Number(isoDay.slice(5, 7));
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${isoDay.slice(0, 7)}-${String(last).padStart(2, "0")}`;
}

/** The days the view shows: one, a Monday-first week, or the month padded to whole weeks. */
export function rangeFor(mode: CalendarMode, anchor: string): { from: string; to: string } {
  if (mode === "day") return { from: anchor, to: anchor };
  if (mode === "week") return { from: weekStart(anchor), to: addIsoDays(weekStart(anchor), 6) };
  const from = weekStart(monthStart(anchor));
  const end = monthEnd(anchor);
  return { from, to: addIsoDays(weekStart(end), 6) };
}

export function daysOf(range: { from: string; to: string }): string[] {
  const out: string[] = [];
  for (let d = range.from; d <= range.to; d = addIsoDays(d, 1)) out.push(d);
  return out;
}

/** Previous / next period. A month step keeps the day inside the target month. */
export function shiftAnchor(mode: CalendarMode, anchor: string, dir: -1 | 1): string {
  if (mode === "day") return addIsoDays(anchor, dir);
  if (mode === "week") return addIsoDays(anchor, 7 * dir);
  const y = Number(anchor.slice(0, 4));
  const m = Number(anchor.slice(5, 7)) - 1 + dir;
  const first = new Date(Date.UTC(y, m, 1));
  return first.toISOString().slice(0, 10);
}

const fmt = (opts: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat("es-MX", { timeZone: "UTC", ...opts });
const at = (iso: string) => new Date(`${iso}T12:00:00Z`);
const monthYear = fmt({ month: "long", year: "numeric" });
const dayMonth = fmt({ day: "numeric", month: "long" });
const longDay = fmt({ weekday: "long", day: "numeric", month: "long", year: "numeric" });
const shortWeekday = fmt({ weekday: "short" });

const clean = (s: string) => s.replace(/[.,]/g, "").replace(/\s+/g, " ").trim();
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** "Martes 3 de noviembre de 2026", "Semana del 2 al 8 de noviembre", "Noviembre de 2026". */
export function periodLabel(mode: CalendarMode, anchor: string): string {
  if (mode === "day") return cap(clean(longDay.format(at(anchor))));
  if (mode === "month") return cap(clean(monthYear.format(at(anchor))));
  const { from, to } = rangeFor("week", anchor);
  const sameMonth = from.slice(0, 7) === to.slice(0, 7);
  return sameMonth
    ? `Semana del ${Number(from.slice(8))} al ${clean(dayMonth.format(at(to)))}`
    : `Semana del ${clean(dayMonth.format(at(from)))} al ${clean(dayMonth.format(at(to)))}`;
}

/** "lun" … "dom". */
export function weekdayShort(isoDay: string): string {
  return clean(shortWeekday.format(at(isoDay)));
}

/** How one due study reads: the state the counter cares about, overdue first. */
export function dueChip(it: Pick<CalendarDueItem, "status" | "item_state" | "overdue">): { label: string; tone: ChipTone } {
  if (it.overdue) return { label: it.item_state === "captured" ? "Atrasado · por validar" : "Atrasado", tone: "red" };
  if (it.status === "delivered") return { label: "Entregado", tone: "blue" };
  if (it.status === "ready") return { label: "Listo", tone: "green" };
  if (it.item_state === "captured") return { label: "Por validar", tone: "amber" };
  return { label: "Pendiente", tone: "neutral" };
}

/** Where tapping a study goes: its capture while there is work for a chemist, Entrega once ready, else the order. */
export function dueTarget(it: Pick<CalendarDueItem, "status" | "order_item_id" | "order_id" | "item_state">, canCapture: boolean): string {
  if (it.status === "pending" && canCapture) return `/captura/${it.order_item_id}`;
  if (it.status === "ready") return `/entrega/${it.order_id}`;
  return `/solicitudes/${it.order_id}`;
}

export function byDay<T>(items: T[], dayOf: (t: T) => string): Map<string, T[]> {
  const m = new Map<string, T[]>();
  for (const it of items) m.set(dayOf(it), [...(m.get(dayOf(it)) ?? []), it]);
  return m;
}

/** What one day holds, after the "same-day studies" filter. */
export interface DayContent {
  due: CalendarDueItem[];
  entries: CalendarOccurrence[];
}

export function visibleDue(items: CalendarDueItem[], includeSameDay: boolean): CalendarDueItem[] {
  return includeSameDay ? items : items.filter((i) => !i.same_day);
}

/** Month cell summary: how many due, how many still pending (and whether any is overdue). */
export function daySummary(due: CalendarDueItem[]): { total: number; pending: number; overdue: number } {
  return {
    total: due.length,
    pending: due.filter((d) => d.status === "pending").length,
    overdue: due.filter((d) => d.overdue).length,
  };
}

/**
 * Which branch a calendar shows (Fer, 2026-11-03: two branch selectors on one page confused her).
 * It always follows the app's branch selector; the only extra choice is «Ver ambas sucursales».
 */
export function calendarScope(both: boolean, branch: { id: string; name: string }, branches: readonly { id: string }[]): { branchId: string | null; label: string; toggleLabel: string } {
  const all = branches.length === 2 ? "Ambas sucursales" : "Todas las sucursales";
  return {
    branchId: both && branches.length > 1 ? null : branch.id,
    label: both && branches.length > 1 ? all : branch.name,
    toggleLabel: branches.length === 2 ? "Ver ambas sucursales" : "Ver todas las sucursales",
  };
}
